import React, { useEffect, useState, useRef } from "react";
import { withStyles } from "@mui/styles";
import { styleSheet } from "../style.js";
import { Button, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import TransactionHeader from "./transactionHeader.jsx";
import CanvasRenderer from "./canvasRender.jsx";
import DragDropHandler from "./dragDropHandler.jsx";
import FieldManager, { createSvgIcon } from "./fieldManager.jsx";
import NavigationButtons from "./navigationButtons.jsx";
import RightSideDrawer from "./RightSideDrawer.jsx";

const FormDisplay = ({
  classes,
  getFormData,
  formData,
  setFormData,
  setActivePage,
  activePage,
}) => {
  const { id, form_id } = useParams();
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState([]);
  const [bindFieldsCount, setbindFieldsCount] = useState(0);
  const isRendered = useRef(false);
  const docDetail = useSelector((item) => item.Docs.docDetail);
  const canvasRefs = useRef([]);

  // State to track dropped fields
  const [droppedFields, setDroppedFields] = useState([]);
  // State to track dragging status
  const [isDragging, setIsDragging] = useState(false);
  // State to track drawer open status
  const [drawerOpen, setDrawerOpen] = useState(false);
  // State to track selected field
  const [selectedField, setSelectedField] = useState(null);

  let navigate = useNavigate();

  // Scroll handler to determine active page
  useEffect(() => {
    const handleScroll = () => {
      if (!formData?.forms_data?.total_pages) return;

      let maxVisiblePage = 0;
      let maxVisibleArea = 0;

      for (let i = 0; i < formData?.forms_data?.total_pages; i++) {
        const pageElement = document.getElementById(`myCanvas${i}`);
        if (!pageElement) continue;

        const rect = pageElement.getBoundingClientRect();
        const visibleHeight =
          Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

        if (visibleHeight > maxVisibleArea) {
          maxVisibleArea = visibleHeight;
          maxVisiblePage = i;
        }
      }

      setActivePage(maxVisiblePage);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [formData?.forms_data?.total_pages]);

  // Navigation functions
  const redirectToForm = (form_id) => {
    navigate(`/docs/folders/${id}/form/${form_id}/edit`);
  };

  const handlePreviousDocument = (id) => {
    let forms = [...docDetail?.data?.forms];
    let currentIndex = forms?.findIndex((item) => item.id === formData.id);
    if (forms?.length > 1 && currentIndex !== 0 && forms[currentIndex - 1]) {
      redirectToForm(forms[currentIndex - 1].id);
    }
  };

  const handleNextDocument = () => {
    let forms = [...docDetail?.data?.forms];
    let currentIndex = forms?.findIndex((item) => item.id === formData.id);
    if (
      forms?.length > 1 &&
      currentIndex !== forms?.length - 1 &&
      forms[currentIndex + 1]
    ) {
      redirectToForm(forms[currentIndex + 1].id);
    }
  };

  const isDisabled = (type) => {
    if (docDetail?.data?.forms?.length) {
      let forms = [...docDetail?.data?.forms];
      let currentIndex = forms?.findIndex((item) => item.id === formData.id);

      if (type === "next") {
        if (
          docDetail?.data?.forms?.length < 2 ||
          currentIndex === forms?.length - 1 ||
          !forms[currentIndex + 1]
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        if (
          docDetail?.data?.forms?.length < 2 ||
          currentIndex === 0 ||
          !forms[currentIndex - 1]
        ) {
          return true;
        } else {
          return false;
        }
      }
    } else {
      return true;
    }
  };

  // Handle field selection and drawer open/close
  useEffect(() => {
    const selectedField = droppedFields.find((field) => field.isSelected);
    setSelectedField(selectedField);
    setDrawerOpen(!!selectedField);
  }, [droppedFields]);

  // Handle assigning user to field
  const handleAssignUser = (fieldId, userId, user) => {
    // Update the user name on the canvas element
    const element = document.getElementById(fieldId);

    if (element) {
      let field = droppedFields?.find((fi) => fi.id === fieldId);
      if (field.tabType === "Signature") {
        element.children[1].innerText = `${user.first_name} ${user.last_name}`;
      }
      if (field.tabType === "Initial") {
        element.children[1].innerText = `${user.first_name?.slice(
          0,
          1
        )}${user.last_name?.slice(0, 1)}`;
      }
    }

    // Update state
    setDroppedFields((prev) => {
      return prev.map((field) => {
        if (field.id === fieldId) {
          return { ...field, user: user };
        }
        return field;
      });
    });
  };

  // Handle deleting a field
  const handleDeleteField = (fieldId) => {
    // Remove the element from the DOM
    const element = document.getElementById(fieldId);
    if (element) {
      element.parentNode.removeChild(element);
    }

    // Remove from state
    setDroppedFields((prev) => prev.filter((field) => field.id !== fieldId));
    setDrawerOpen(false);
  };

  // Handle done button click
  const handleDone = (fieldId) => {
    // Here you can save the field data or perform any other action
    setDrawerOpen(false);
    // Deselect the field
    setDroppedFields((prev) => {
      return prev.map((field) => {
        if (field.id === fieldId) {
          return {
            ...field,
            isSelected: false,
            borderColor:
              field.borderColor === "#2868d7" ? "#ff9999" : field.borderColor,
          };
        }
        return field;
      });
    });
  };

  // Handle updating field value
  const handleUpdateValue = (fieldId, value) => {
    // Update the value on the canvas
    const element = document.getElementById(fieldId);
    if (element) {
      // For TextBox and Date fields, update the innerHTML
      const field = droppedFields.find((field) => field.id === fieldId);
      if (field) {
        if (field.tabType === "TextBox" || field.tabType === "Date") {
          const fieldType = field.tabType;
          const fieldLabel = fieldType === "TextBox" ? "TextBox" : "Date";
          const svgIcon =
            fieldType === "Date"
              ? `<div style="display: flex; align-items: center; justify-content: center; height: 16px; width: 20px;">${createSvgIcon(
                  "Date"
                )}</div>`
              : "";

          element.innerHTML = `${
            !value
              ? `${
                  fieldType === "Date" ? svgIcon : ""
                }<div style="font-size: 10px; line-height: 12px; text-align: center;">${fieldLabel}</div>`
              : ""
          }
          ${
            value
              ? `<div style="font-size: 12px; line-height: 14px; text-align: center; max-height: 60px; overflow: hidden; text-overflow: ellipsis;">${value}</div>`
              : ""
          }`;
        } else if (field.tabType === "Checkbox") {
          // For Checkbox fields, update the checked state
          const checkboxInput = element.querySelector('input[type="checkbox"]');
          if (checkboxInput) {
            checkboxInput.checked = value === true;
          }
        }
      }
    }

    // Update the state
    setDroppedFields((prev) => {
      return prev.map((field) => {
        if (field.id === fieldId) {
          return { ...field, value };
        }
        return field;
      });
    });
  };
  console.log(droppedFields, "droppedFields");

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // Deselect all fields
    setDroppedFields((prev) => {
      return prev.map((field) => {
        return {
          ...field,
          isSelected: false,
          borderColor:
            field.borderColor === "#2868d7" ? "#ff9999" : field.borderColor,
        };
      });
    });
  };

  return (
    <>
      <TransactionHeader classes={classes} docDetail={docDetail} />

      <Box className={classes.transactionContainer}>
        <NavigationButtons
          classes={classes}
          isDisabled={isDisabled}
          onPrevious={handlePreviousDocument}
          onNext={handleNextDocument}
          position="top"
        />

        <CanvasRenderer
          classes={classes}
          formData={formData}
          activePage={activePage}
          canvasRefs={canvasRefs}
          bindFieldsCount={bindFieldsCount}
          setbindFieldsCount={setbindFieldsCount}
          isRendered={isRendered}
          formValues={formValues}
          droppedFields={droppedFields}
          setDroppedFields={setDroppedFields}
        />

        <NavigationButtons
          classes={classes}
          isDisabled={isDisabled}
          onPrevious={handlePreviousDocument}
          onNext={handleNextDocument}
          position="bottom"
        />
      </Box>

      <DragDropHandler
        formData={formData}
        droppedFields={droppedFields}
        setDroppedFields={setDroppedFields}
        isDragging={isDragging}
        setIsDragging={setIsDragging}
      />

      <FieldManager
        droppedFields={droppedFields}
        setDroppedFields={setDroppedFields}
        activePage={activePage}
      />

      <RightSideDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        selectedField={selectedField}
        onAssignUser={handleAssignUser}
        onDelete={handleDeleteField}
        onDone={handleDone}
        onUpdateValue={handleUpdateValue}
      />
    </>
  );
};

export default withStyles(styleSheet, { name: "FormDisplayStyle" })(
  FormDisplay
);
